import React, { useState } from 'react';
import { Palette } from 'lucide-react';
import './WebsiteInput.css';

const WebsiteInput = ({ organizerId, onComplete, onSkip }) => {
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!websiteUrl.trim()) {
      setError('Please enter a website URL');
      return;
    }

    setError('');
    setLoading(true);
    setAnalyzing(true);

    try {
      const response = await fetch(`/api/organizer/${encodeURIComponent(organizerId)}/analyze-website`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ website_url: websiteUrl }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Pass the extracted branding data to the parent
        onComplete(data.branding);
      } else {
        setError(data.error || 'Failed to analyze website. Please try again.');
        setAnalyzing(false);
      }
    } catch (err) {
      console.error('Error analyzing website:', err);
      setError('Network error. Please try again.');
      setAnalyzing(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.iconContainer}>
            <Palette size={32} style={styles.icon} />
          </div>
          <h2 style={styles.title}>Brand Your Experience</h2>
          <p style={styles.subtitle}>
            Enter your website URL and we'll automatically extract your branding (logo, colors, fonts) 
            to match your brand identity.
          </p>
        </div>

        {analyzing && (
          <div style={styles.analyzingOverlay}>
            <div style={styles.analyzingContent}>
              <div style={styles.spinner}></div>
              <h3 style={styles.analyzingTitle}>Analyzing Your Website...</h3>
              <p style={styles.analyzingText}>Our AI is visiting your website and extracting your branding information.</p>
              <div style={styles.analyzingDetails}>
                <span>🔍 Scanning website structure...</span>
                <span>🎨 Extracting colors and fonts...</span>
                <span>🖼️ Finding your logo...</span>
                <span>✨ Preparing your brand setup...</span>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formGroup}>
            <label htmlFor="website-url" style={styles.label}>Your Website URL</label>
            <div style={{
              ...styles.inputWrapper,
              ...(isInputFocused ? styles.inputWrapperFocus : {})
            }}>
              <span style={styles.inputPrefix}>https://</span>
              <input
                id="website-url"
                type="text"
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
                placeholder="yourcompany.com"
                disabled={loading || analyzing}
                onFocus={() => setIsInputFocused(true)}
                onBlur={() => setIsInputFocused(false)}
                style={{
                  ...styles.input,
                  ...(error ? styles.inputError : {})
                }}
              />
            </div>
            {error && <div style={styles.errorMessage}>{error}</div>}
            <small style={styles.helpText}>
              We analyze your website's design and apply branding automatically
            </small>
          </div>

          <div style={styles.formActions}>
            <button
              type="button"
              onClick={onSkip}
              style={styles.skipButton}
              disabled={loading || analyzing}
              onMouseEnter={(e) => {
                if (!loading && !analyzing) {
                  e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading && !analyzing) {
                  e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                  e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                }
              }}
            >
              Skip for now
            </button>
            <button
              type="submit"
              disabled={loading || analyzing || !websiteUrl.trim()}
              style={{
                ...styles.analyzeButton,
                ...((loading || analyzing || !websiteUrl.trim()) ? styles.buttonDisabled : {})
              }}
              onMouseEnter={(e) => {
                if (!loading && !analyzing && websiteUrl.trim()) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 10px 25px rgba(139, 92, 246, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading && !analyzing && websiteUrl.trim()) {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 20px rgba(139, 92, 246, 0.3)';
                }
              }}
            >
              {loading ? 'Analyzing...' : 'Analyze & Apply Branding'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100%',
    width: '100%',
    padding: '40px 20px',
  },
  card: {
    background: 'rgba(30, 41, 59, 0.8)',
    backdropFilter: 'blur(20px)',
    borderRadius: '20px',
    padding: '48px',
    maxWidth: '600px',
    width: '100%',
    position: 'relative',
    overflow: 'hidden',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5), 0 0 100px rgba(99, 102, 241, 0.1)',
  },
  header: {
    textAlign: 'center',
    marginBottom: '40px',
  },
  iconContainer: {
    width: '64px',
    height: '64px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.2) 0%, rgba(59, 130, 246, 0.2) 50%, rgba(234, 179, 8, 0.2) 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
    position: 'relative',
    overflow: 'hidden',
  },
  icon: {
    color: '#ffffff',
    filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))',
    zIndex: 1,
  },
  title: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#ffffff',
    margin: '0 0 16px 0',
    lineHeight: '1.2',
  },
  subtitle: {
    fontSize: '16px',
    color: 'rgba(255, 255, 255, 0.8)',
    lineHeight: '1.6',
    margin: 0,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '32px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#ffffff',
  },
  inputWrapper: {
    display: 'flex',
    alignItems: 'center',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '10px',
    overflow: 'hidden',
    background: 'rgba(15, 23, 42, 0.5)',
    transition: 'all 0.3s ease',
  },
  inputWrapperFocus: {
    borderColor: 'rgba(99, 102, 241, 0.5)',
    boxShadow: '0 0 0 3px rgba(99, 102, 241, 0.1)',
  },
  inputPrefix: {
    padding: '14px 16px',
    background: 'rgba(255, 255, 255, 0.05)',
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: '15px',
    borderRight: '1px solid rgba(255, 255, 255, 0.1)',
    fontWeight: '500',
  },
  input: {
    flex: 1,
    border: 'none',
    padding: '14px 16px',
    fontSize: '16px',
    outline: 'none',
    background: 'transparent',
    color: '#ffffff',
  },
  inputError: {
    color: '#f87171',
  },
  errorMessage: {
    color: '#f87171',
    fontSize: '14px',
    marginTop: '4px',
  },
  helpText: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: '13px',
    marginTop: '4px',
  },
  formActions: {
    display: 'flex',
    gap: '12px',
    marginTop: '8px',
  },
  skipButton: {
    flex: 1,
    padding: '14px 24px',
    fontSize: '15px',
    fontWeight: '600',
    borderRadius: '10px',
    border: '2px solid rgba(255, 255, 255, 0.2)',
    background: 'rgba(255, 255, 255, 0.05)',
    color: '#ffffff',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  analyzeButton: {
    flex: 1,
    padding: '14px 24px',
    fontSize: '15px',
    fontWeight: '600',
    borderRadius: '10px',
    border: 'none',
    background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
    color: '#ffffff',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 20px rgba(139, 92, 246, 0.3)',
  },
  buttonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
    transform: 'none',
  },
  analyzingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(15, 23, 42, 0.98)',
    backdropFilter: 'blur(10px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
    borderRadius: '20px',
  },
  analyzingContent: {
    textAlign: 'center',
    padding: '40px',
  },
  spinner: {
    width: '60px',
    height: '60px',
    border: '4px solid rgba(255, 255, 255, 0.1)',
    borderTopColor: '#8b5cf6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 24px',
  },
  analyzingTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#ffffff',
    margin: '0 0 12px 0',
  },
  analyzingText: {
    fontSize: '16px',
    color: 'rgba(255, 255, 255, 0.8)',
    margin: '8px 0',
  },
  analyzingDetails: {
    marginTop: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    alignItems: 'flex-start',
  },
};

export default WebsiteInput;

